import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Menu, MenuItem } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DashboardTabFilter from "./reuseable-components/DashboardTabFilter";
import DashBoardTable from "./reuseable-components/DashBoardTable";
import ConfirmModal from "./reuseable-components/ConfirmModal";
import {
  downloadCSV,
  downloadExcel,
  downloadPDF,
} from "../../utils/exportUtils";
import { deleteStory } from "../../apis/admin-apis/dashboard-apis/FamilyStoriesTabApis";

const FamilyStoriesTab = ({ storiesData = [], refreshStories }) => {
  // 1. State Management
  const [filters, setFilters] = useState({
    search: "",
    from_date: "",
    to_date: "",
    zip_code: "",
    source: "All Sources",
  });

  const [sortConfig, setSortConfig] = useState({
    key: "submitted_date",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [confirmState, setConfirmState] = useState({
    open: false,
    storyId: null,
  });

  const open = Boolean(anchorEl);

  // 2. Column Definitions
  const columns = [
    { id: "name", label: "Name", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "submitted_date", label: "Submitted Date", sortable: true },
    { id: "source", label: "Source", sortable: false },
    { id: "actions", label: "Actions", sortable: false },
  ];

  const formatStoryDate = (value) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString();
  };

  const exportColumns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    { id: "submitted_date", label: "Submitted Date" },
    { id: "source", label: "Source" },
    { id: "message", label: "Message" },
  ];

  const stories = useMemo(
    () =>
      storiesData.map((story) => ({
        id: story._id,
        name: story.name || "-",
        email: story.email || "-",
        message: story.message || "-",
        zipCode: story.zipCode || "-",
        source: story.source || "-",
        submitted_date: formatStoryDate(story.submittedAt),
      })),
    [storiesData],
  );

  // 3. Filtering and Sorting
  const filteredRows = useMemo(() => {
    let data = [...stories];

    if (filters.search) {
      const search = filters.search.toLowerCase();

      data = data.filter(
        (item) =>
          item.name?.toLowerCase().includes(search) ||
          item.email?.toLowerCase().includes(search) ||
          item.source?.toLowerCase().includes(search),
      );
    }

    if (filters.from_date) {
      data = data.filter(
        (item) => new Date(item.submittedAt) >= new Date(filters.from_date),
      );
    }

    if (filters.to_date) {
      data = data.filter(
        (item) => new Date(item.submittedAt) <= new Date(filters.to_date),
      );
    }

    if (sortConfig.key) {
      data.sort((a, b) => {
        let valueA = a[sortConfig.key];
        let valueB = b[sortConfig.key];

        if (sortConfig.key === "submitted_date") {
          valueA = new Date(a.submittedAt);
          valueB = new Date(b.submittedAt);
        }

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }

        return 0;
      });
    }

    return data;
  }, [filters, sortConfig, stories]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDeleteConfirm = (storyId) => {
    setConfirmState({
      open: true,
      storyId,
    });
  };

  const handleCloseConfirm = () => {
    setConfirmState({
      open: false,
      storyId: null,
    });
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteStory(confirmState.storyId);
      if (refreshStories) {
        refreshStories();
      }
    } catch (error) {
      console.error("Failed to delete story:", error);
    } finally {
      setIsDeleting(false);
      handleCloseConfirm();
    }
  };

  // 4. Transform Data for Table Component
  const rows = paginatedRows.map((row) => ({
    ...row,
    name: (
      <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
        {row.name}
      </Typography>
    ),
    actions: (
      <Button
        variant="outlined"
        size="small"
        onClick={() => handleOpenDeleteConfirm(row.id)}
        startIcon={<DeleteIcon sx={{ fontSize: "14px !important" }} />}
        sx={{
          color: "#333",
          borderColor: "#ccc",
          textTransform: "none",
          fontSize: "12px",
          bgcolor: "#f5f5f5",
          height: "26px",
          "&:hover": {
            bgcolor: "#e0e0e0",
            borderColor: "#bbb",
          },
        }}
      >
        Delete
      </Button>
    ),
  }));

  // 5. Handlers
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      from_date: "",
      to_date: "",
      zip_code: "",
      source: "All Sources",
    });
    setPage(1);
  };

  return (
    <Box
      className="family-stories-container"
      sx={{
        p: {
          xs: 1,
          md: 3,
        },
        bgcolor: "#fff",
      }}
    >
      {/* Header Section */}
      <Box
        className="tab-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F1E3D" }}>
          Family Story Submissions
        </Typography>
        <>
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={handleExportClick}
            sx={{
              bgcolor: "#D4A017",
              "&:hover": { bgcolor: "#B8860B" },
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
              px: 2,
            }}
          >
            Export
          </Button>

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                downloadCSV({
                  data: filteredRows,
                  fileName: "family-story-submissions",
                });
                handleClose();
              }}
            >
              Download CSV
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadExcel({
                  data: filteredRows,
                  fileName: "family-story-submissions",
                  sheetName: "Family Story Submissions",
                });
                handleClose();
              }}
            >
              Download Excel
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadPDF({
                  data: filteredRows,
                  fileName: "family-story-submissions",
                  title: "Family Story Submissions",
                  columns: exportColumns,
                });
                handleClose();
              }}
            >
              Download PDF
            </MenuItem>
          </Menu>
        </>
      </Box>

      <Box sx={{ borderBottom: "1px solid #EAE2D1", mb: 3 }} />

      <DashboardTabFilter
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {/* Table Section */}
      <Box className="table-wrapper" sx={{ mt: 2 }}>
        <DashBoardTable
          columns={columns}
          rows={rows}
          sortConfig={sortConfig}
          onSort={(key) =>
            setSortConfig({
              key,
              direction:
                sortConfig.key === key && sortConfig.direction === "asc"
                  ? "desc"
                  : "asc",
            })
          }
          page={page}
          totalPages={Math.ceil(filteredRows.length / rowsPerPage)}
          onPageChange={setPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(v) => {
            setRowsPerPage(v);
            setPage(1);
          }}
          totalResults={filteredRows.length}
        />
      </Box>
      <ConfirmModal
        open={confirmState.open}
        handleClose={handleCloseConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Family Story"
        message="Are you sure you want to delete this family story submission?"
        confirmLabel="Delete"
        loading={isDeleting}
      />
    </Box>
  );
};

export default FamilyStoriesTab;
